import { ClientEvents } from 'common/events/ClientEvents';
import { StorageKeys } from 'common/storage/constants';
import { Slugs } from 'common/urls/Slugs';
import { isNil } from 'lodash';
import QueryString from 'query-string';
import { useContext } from 'react';
import { useHistory } from 'react-router';
import { useMealPlanSurfacingLogic } from 'src/pages/consumer/meal-plans/hooks/useMealPlanSurfacingLogic';
import { MealPlanSubscriptionContext } from 'src/pages/consumer/meal-plans/MealPlanSubscriptionsContext';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useTracker } from 'src/shared/hooks/useTracker';
import Notifications from 'src/shared/Notifications';
import { Routes } from 'src/shared/Routes';
import { getToken, useLocalStorage } from 'src/shared/storage/Storage';
import { getFullRelativeUrl, getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';

/**
 * LAYER 2 of the two-layer zip code redirect architecture.
 *
 * This hook provides REACTIVE redirects based on global state changes.
 * It continuously monitors cached zip code and user state, triggering
 * navigation when conditions are met.
 *
 * PRIMARY USE CASE:
 * - Homepage meal plan button sets redirectOnSubmit=false
 * - useZipCodeInput caches zip but doesn't redirect
 * - This hook detects the new cached zip and triggers appropriate redirect
 *
 * REDIRECT PRIORITY:
 * 1. Active meal plan users → upcoming order page
 * 2. Meal plan intake flow → meal plan preferences (region-specific)
 * 3. Available zip codes → explore page
 *
 * This reactive pattern allows the homepage to maintain control over
 * meal plan routing logic while still leveraging the shared zip input system.
 *
 * @param exploreQueryParams - Query parameters to include in explore redirects
 */
export const useHomepageRedirects = ({ exploreQueryParams }) => {
  const { location } = useHistory();
  const token = getToken();
  const history = useHistory();
  const tracker = useTracker();
  const [cachedZipCode, , zipCodeMetaData] = useCachedZipCode();
  const [currentUser, currentUserLoading] = useCurrentUser();
  const { mealPlanDataLoaded } = useContext(MealPlanSubscriptionContext);
  const subscriptionLoaded = mealPlanDataLoaded || !currentUser;
  const dataLoaded = !currentUserLoading && subscriptionLoaded;
  const [visitedExplorePage] = useLocalStorage<boolean>(StorageKeys.VISITED_EXPLORE_PAGE, false);
  const originalUrl = getFullRelativeUrl(location);

  const {
    getMealPlanUrl,
    visitedMealPlans,
    isInMealPlanRegion,
    mealPlanHasBeenRedirected,
    setMealPlanHasBeenRedirected,
    intakeCompletedAtDate,
  } = useMealPlanSurfacingLogic();

  // Determine if we should redirect to explore based on cached zip or user preferences
  const shouldRedirectToExplore =
    Boolean(
      token && currentUser && currentUser.lastSearchedZipCode && currentUser.lastSearchedZipCodeIsAvailableToCustomers
    ) || Boolean(cachedZipCode && zipCodeMetaData?.cachedZipCodeData?.zipCode?.availableToCustomers);

  // 10/17/24, our new homepage renders before mealPlanDataLoaded. This leads to a pageview on shef.com
  //  right before a redirect, which gives us bad data. This if should redirect on home for those users.
  if (currentUser?.hasActiveMealPlan && !mealPlanHasBeenRedirected) {
    setMealPlanHasBeenRedirected();
    const path = getSluggedPath(Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER, [], {
      ...QueryString.parse(location.search),
      ...exploreQueryParams,
    });
    history.push(path);
  }

  if (!dataLoaded) {
    return;
  }

  const userHasCompletedIntakeForm = !isNil(intakeCompletedAtDate);
  if (
    isInMealPlanRegion &&
    !mealPlanHasBeenRedirected &&
    ((visitedMealPlans && !visitedExplorePage) || userHasCompletedIntakeForm)
  ) {
    let intakeQueryParams = {};
    const mealPlanUrl = getMealPlanUrl({});

    if (mealPlanUrl?.includes(Routes.CONSUMER_MEAL_PLANS_PREFERENCES)) {
      setMealPlanHasBeenRedirected();
      // Extract possible query params from the route mealPlanIntakeRouteByRegion
      const { query } = QueryString.parseUrl(mealPlanUrl);
      intakeQueryParams = { ...query };
    }

    const redirectUrl = getSluggedPath(mealPlanUrl, [], {
      ...QueryString.parse(location.search),
      ...exploreQueryParams,
      ...intakeQueryParams,
    });

    tracker.track(ClientEvents.HOMEPAGE_REDIRECT, {
      intakeCompletedAtDate,
      isInMealPlanRegion,
      mealPlanHasBeenRedirected,
      originalUrl,
      redirectUrl,
      visitedExplorePage,
      visitedMealPlans,
      zipCode: cachedZipCode,
    });
    history.push(redirectUrl);
    return;
  }

  if (shouldRedirectToExplore) {
    // Check if user intended to access meal plans but region doesn't support them
    const currentParams = QueryString.parse(location.search);
    const hasMealPlanIntent = currentParams.mealPlanIntent === 'true';

    if (hasMealPlanIntent && !isInMealPlanRegion) {
      Notifications.info('Our Weekly Shef Service is not yet available in your area. Browse one-time ordering.');
    }

    // Remove mealPlanIntent from query params for clean URL
    const cleanParams = { ...currentParams };
    delete cleanParams.mealPlanIntent;

    const redirectUrl = getSluggedPath(
      Routes.CONSUMER_EXPLORE,
      [
        {
          slug: Slugs.ZIP_CODE,
          value: currentUser?.lastSearchedZipCode ?? cachedZipCode ?? '',
        },
      ],
      { ...cleanParams, ...exploreQueryParams }
    );

    tracker.track(ClientEvents.HOMEPAGE_REDIRECT, {
      intakeCompletedAtDate,
      isInMealPlanRegion,
      mealPlanHasBeenRedirected,
      originalUrl,
      redirectUrl,
      visitedExplorePage,
      visitedMealPlans,
      zipCode: cachedZipCode,
    });

    history.push(redirectUrl);
  }
};
